import { useMemo } from "react";
import { useShallow } from "zustand/shallow";
import { useQueryClient } from "@tanstack/react-query";
import { useGetMerchantWalletBalance } from "../../../../hooks/queries/useMerchantQueries";
import {
  useGetManualEntryCharges,
  useCreateManualEntryBatch,
  bulkUploadsQueryKeys,
} from "../../../../hooks/queries/useBulkUploadsQueries";
import { useBankOptions } from "../../../../hooks/queries/useBanksQueries";
import { TransactionQueryKeys } from "../../../../hooks/queries/useTransactionQueries";
import { toastService } from "../../../../providers";
import { extractAxiosErrorMessage } from "../../../../services/utils/errorUtils";
import useManualEntryStore from "../../../../stores/manualEntryStore";
import {
  MAX_MANUAL_ENTRIES,
  MANUAL_ENTRY_CHANNELS,
} from "../config/manualEntryConfig";

const EMPTY_FORM = (channel) => ({
  recipientName: "",
  phoneNumber: "",
  bankCode: "",
  accountNumber: "",
  amount: "",
  remarks: "",
  channel: channel ?? MANUAL_ENTRY_CHANNELS.MPESA,
});

const buildEntryFields = (fields, channel) => {
  const {
    recipientName,
    phoneNumber,
    bankCode,
    accountNumber,
    amount,
    remarks,
  } = fields;
  if (channel === MANUAL_ENTRY_CHANNELS.BANK) {
    return { recipientName, accountNumber, bankCode, amount, remarks };
  }
  return { recipientName, phoneNumber, amount, remarks };
};

const buildSubmitPayload = (entries, paymentChannel) => ({
  paymentChannel,
  recipients: entries.map((entry) => {
    if (paymentChannel === MANUAL_ENTRY_CHANNELS.BANK) {
      return {
        recipientName: entry.recipientName,
        accountNumber: entry.accountNumber,
        bankCode: entry.bankCode,
        amount: Number.parseFloat(Number(entry.amount).toFixed(2)),
        remarks: entry.remarks,
      };
    }
    return {
      recipientName: entry.recipientName,
      phoneNumber: entry.phoneNumber,
      amount: Number.parseFloat(Number(entry.amount).toFixed(2)),
      remarks: entry.remarks,
    };
  }),
});

export const useManualEntry = () => {
  const queryClient = useQueryClient();

  const { data: walletBalanceData, isError: isWalletApiError } =
    useGetMerchantWalletBalance();

  const apiWalletBalance = walletBalanceData?.balance ?? null;

  const {
    paymentChannel,
    merchantBalance,
    entries,
    editingId,
    totalCharges,
    totalDisbursement,
  } = useManualEntryStore(
    useShallow((s) => ({
      paymentChannel: s.paymentChannel,
      merchantBalance: s.merchantBalance,
      entries: s.entries,
      editingId: s.editingId,
      totalCharges: s.runningTotals.totalCharges,
      totalDisbursement: s.runningTotals.totalDisbursement,
    })),
  );

  const { mutate: fetchCharges, isPending: isSubmitting } =
    useGetManualEntryCharges();

  const { mutate: submitBatch, isPending: isSubmittingBatch } =
    useCreateManualEntryBatch();

  const {
    banks,
    bankOptions,
    bankFilter,
    isBanksLoading,
    isBanksError,
    refetchBanks,
  } = useBankOptions();

  // Charges response is always preferred (freshest server value).
  // Fall back to the standalone wallet API only before the first entry is added.
  const resolvedBalance = useMemo(
    () => merchantBalance ?? apiWalletBalance,
    [merchantBalance, apiWalletBalance],
  );

  // Only surface the error when both sources are unavailable.
  const isBalanceError = isWalletApiError && merchantBalance === null;

  const totalPlusFees = totalDisbursement + totalCharges;
  const editingEntry = entries.find((e) => e.id === editingId);
  const editingEntryTotalCost = editingEntry
    ? (editingEntry.amount ?? 0) + (editingEntry.charges ?? 0)
    : 0;
  const totalRecipients = entries.length || null;
  const isAtLimit = entries.length >= MAX_MANUAL_ENTRIES;
  const isChannelLocked = entries.length > 0 && !!paymentChannel;

  const handleAddToList = (formData, resetForm) => {
    const { channel, ...recipientFields } = formData;
    const entryFields = buildEntryFields(recipientFields, channel);

    fetchCharges(
      { amount: entryFields.amount, paymentChannel: channel },
      {
        onSuccess: (data) => {
          const store = useManualEntryStore.getState();
          store.setPaymentChannel(channel);
          store.setMerchantBalance(data.walletBalance ?? null);
          store.addEntry({
            id: crypto.randomUUID(),
            ...entryFields,
            charges: data.totalCharge,
          });
          if (channel === MANUAL_ENTRY_CHANNELS.BANK) {
            store.resetBankVerification();
          } else {
            store.resetPhoneVerification();
          }
          resetForm(EMPTY_FORM(channel));
          toastService.success("Recipient added to batch.");
        },
        onError: (error) => {
          toastService.error(
            extractAxiosErrorMessage(
              error,
              "Failed to compute charges. Please try again.",
            ),
          );
        },
      },
    );
  };

  const handleSubmitBatch = (onSuccess, resetForm) => {
    const store = useManualEntryStore.getState();
    store.clearEntryErrors();

    submitBatch(buildSubmitPayload(store.entries, store.paymentChannel), {
      onSuccess: (data) => {
        if (data.success === true) {
          store.reset();
          resetForm(EMPTY_FORM(""));
          queryClient.invalidateQueries({ queryKey: bulkUploadsQueryKeys.all });
          queryClient.invalidateQueries({ queryKey: TransactionQueryKeys.all });
          onSuccess(data);
        } else {
          store.applyRowErrors(data.recipients ?? []);
          resetForm(EMPTY_FORM(store.paymentChannel));
          toastService.error(
            "Some entries failed validation. Check the table for details.",
          );
        }
      },
      onError: (error) => {
        toastService.error(
          extractAxiosErrorMessage(
            error,
            "Batch submission failed. Please try again.",
          ),
        );
      },
    });
  };

  const handleUpdateEntry = (formData, resetForm) => {
    const { channel, ...recipientFields } = formData;
    const entryFields = buildEntryFields(recipientFields, channel);
    const id = useManualEntryStore.getState().editingId;

    fetchCharges(
      { amount: entryFields.amount, paymentChannel: channel },
      {
        onSuccess: (data) => {
          const store = useManualEntryStore.getState();
          store.updateEntry(id, { ...entryFields, charges: data.totalCharge });
          store.setMerchantBalance(data.walletBalance ?? null);
          store.clearEditing();
          if (channel === MANUAL_ENTRY_CHANNELS.BANK) {
            store.resetBankVerification();
          } else {
            store.resetPhoneVerification();
          }
          resetForm(EMPTY_FORM(channel));
          toastService.success("Entry updated.");
        },
        onError: (error) => {
          toastService.error(
            extractAxiosErrorMessage(
              error,
              "Failed to compute charges. Please try again.",
            ),
          );
        },
      },
    );
  };

  return {
    walletBalance: resolvedBalance,
    isBalanceError,
    paymentChannel,
    entries,
    editingId,
    totalCharges,
    totalDisbursement,
    totalPlusFees,
    editingEntryTotalCost,
    totalRecipients,
    isAtLimit,
    isChannelLocked,
    isSubmitting,
    banks,
    bankOptions,
    bankFilter,
    isBanksLoading,
    isBanksError,
    refetchBanks,
    handleAddToList,
    handleUpdateEntry,
    handleSubmitBatch,
    isSubmittingBatch,
  };
};
